import { ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import NoRowsOverlay from "../../../components/NoRowsOverlay/NoRowsOverlay";
import { commodityTokenApi } from "../../../http/commodity-tokens.api";
import { CommodityToken } from "../../../types";
import { toShortHash } from "../../../utils/blockchain-utils";
import { toYesOrNoText } from "../../../utils/coimex-utils";

interface IProps {
  token: CommodityToken;
}

export default function CommodityTokenPauseInfo({ token }: IProps) {
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(4);
  const [expanded, setExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const cols = useMemo<GridColDef[]>(
    () => [
      {
        field: "rowNo",
        headerName: "",
        sortable: false,
        width: 50,
      },
      {
        field: "pause",
        headerName: "Status",
        renderCell: ({ value }) => (value ? "Paused" : "Resumed"),
        sortable: false,
        flex: 1,
      },
      {
        field: "tx",
        headerName: "Tx Hash",
        renderCell: ({ value }) =>
          value ? (
            <a
              className="link"
              href={value.txUrl}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
            >
              {toShortHash(value.txHash)}
            </a>
          ) : null,
        sortable: false,
        flex: 1,
      },
      {
        field: "createdAt",
        headerName: "Timestamp",
        valueFormatter: (params) => moment(params.value).format("YYYY-MM-DD"),
        sortable: false,
        width: 120,
      },
    ],
    []
  );

  const getToggleTokenPauseTxs = async (
    pg: { page: number; pageSize: number } = null
  ) => {
    try {
      setIsLoading(true);
      const { data, total } = await commodityTokenApi.getToggleTokenPauseTxs(
        token.address,
        {
          from: pg?.page * pg?.pageSize,
          take: pg?.pageSize,
        }
      );
      setRows(
        data?.map((toggleTokenPauseTx, index) => ({
          ...toggleTokenPauseTx,
          rowNo: (pg?.page || 0) * (pg?.pageSize || 0) + index + 1,
        })) || []
      );
      setTotal(total);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (expanded) {
      getToggleTokenPauseTxs({ page, pageSize });
    }
  }, [expanded, page, pageSize]);

  return (
    <Accordion
      className="-mx-2 mt-0"
      onChange={(_, expanded) => setExpanded(expanded)}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <div className="info-section-item -mx-1.5">
          Paused
          <span className="info-section-item__value">
            {toYesOrNoText(token.isPaused)}
          </span>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <DataGrid
          columns={cols}
          rows={rows}
          disableColumnMenu
          disableSelectionOnClick
          style={{ minHeight: "100px" }}
          loading={isLoading}
          pagination
          paginationMode="server"
          autoHeight
          pageSize={pageSize}
          rowCount={total}
          onPageChange={(page) => setPage(page)}
          onPageSizeChange={(pageSize) => setPageSize(pageSize)}
          components={{
            NoRowsOverlay: () => <NoRowsOverlay emptyMessage="No record" />,
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
}
